import React, { useEffect, useRef, useState } from 'react'
import constants from '../constants'
import Grid from './grid'
import { gsap } from "gsap";
import { Draggable } from "gsap/Draggable.js"
import { ScrollToPlugin } from "gsap/ScrollToPlugin.js"
import Store from '../store'

gsap.registerPlugin(Draggable, ScrollToPlugin)

const Menu = ({className}) => {
  const wrapperEl = useRef()
  const scrollingEl = useRef()
  const parentEl = useRef()
  const containerEl = useRef()
  const items = useRef()
  const dotEl = useRef()
  const mouse = useRef({ x: 0, y: 0 })
  const infoBlockTl = useRef()
  const infoEl = useRef()
  const anchor = useRef()
  const infoBlockImage = useRef()
  const infoBlockTitle = useRef()
  const repositionTimeout = useRef()
  const pointerToWindowRatio = useRef()
  const [isVisible, setIsVisible] = useState(true)
  const HoverItem = ({photo, text}) => {
    return (
      <div className={`tw-relative tw-w-52`}>
        <div className={`tw-flex`}>
          <div style={{ height: `1px`, top: `5px` }} className={`tw-w-10 tw-bg-gold tw-relative hover-line`}></div>
          <div className={``}>
            <p className={`${constants.heading} tw-uppercase tw-text-gold tw-leading-tight tw-pb-2 tw-relative tw-left-2 hover-title tw-font-sans`}></p>
            <img src={photo} className={`tw-relative tw-left-2 hover-image`}></img>
          </div>
        </div>
      </div>
    )
  }
  const ListItem = ({item}) => {
    const timeoutOver = useRef()
    const listItemEl = useRef()
    const showMore = () => {
      listItemEl.current = items.current.filter(over => over.id === item.anchor)[0]
      infoBlockImage.current.onload = (e) => {
        infoBlockTl.current.timeScale(1).play(0)
      }
      infoBlockImage.current.src = `${IMG_BASEPATH}${item.photo.src}`
      infoBlockTitle.current.innerHTML = item.text
    }
    const overDate = () => {
      timeoutOver.current = setTimeout(showMore, 50)
    }
    const outDate = () => {
      clearTimeout(timeoutOver.current)
      if (infoBlockTl.current) infoBlockTl.current.timeScale(4).reverse()
    }
    return (
      <li data-anchor={item.anchor} className={`${constants.heading} tw-text-gray-600 tw-antialised tw-flex tw-text-right menu-item`} >
        <div className={`tw-flex tw-items-center tw-w-full`}>
          <a
            className={`tw-py-1.5 tw-pl-1 tw-block hover:tw-text-gold tw-text-right tw-w-full hover-date tw-font-sans`}
            href={`#${item.anchor}`}
            onMouseOver={overDate}
            onMouseOut={outDate}
          >
            {item.data}
          </a>
        </div>
      </li>
    )
  }
  useEffect(() => {
    Draggable.create(scrollingEl.current, {
      edgeResistance: 0.1,
      throwProps: true,
      cursor: "auto",
      type: "scrollTop",
      dragClickables: false
    })
    const onResize = () => {
      pointerToWindowRatio.current = gsap.utils.mapRange(0, window.innerHeight, 0, ScrollToPlugin.max(wrapperEl.current, "y"))
    }
    const onMouseMove = (e) => {
      mouse.current.x = e.clientX
      mouse.current.y = e.clientY
      const isLowerThanMiddleWindow = mouse.current.y > window.innerHeight * 0.5
      const offsetScaleY = isLowerThanMiddleWindow ? 1 - (mouse.current.y / (window.innerHeight * 0.5)) * 1 : 0
      const offsetMouseY = Math.abs(offsetScaleY) * 200
      const onUpdate = () => {
        gsap.to(infoEl.current, {
          x: mouse.current.x + 40,
          y: mouse.current.y - 4,
          duration: 0.4,
          ease: "circ",
          overwrite: true
        })
      }
      gsap.to(wrapperEl.current, {
        scrollTo:{ y: pointerToWindowRatio.current(mouse.current.y + offsetMouseY) },
        ease: "expo",
        overwrite: true,
        onUpdate: onUpdate
      })
    }
    setTimeout(onResize, 200)
    window.addEventListener("resize", onResize)
    wrapperEl.current.addEventListener("mousemove", onMouseMove)
    return () => {
      window.removeEventListener("resize", onResize)
      wrapperEl.current.removeEventListener("mousemove", onMouseMove)
    }
  }, [])
  useEffect(() => {
    const menuItems = [...parentEl.current.getElementsByClassName(`menu-item`)]
    const infoBlockLine = infoEl.current.getElementsByClassName(`hover-line`)[0]
    infoBlockTitle.current = infoEl.current.getElementsByClassName(`hover-title`)[0]
    infoBlockImage.current = infoEl.current.getElementsByClassName(`hover-image`)[0]
    infoBlockTl.current = gsap.timeline()
    infoBlockTl.current.fromTo(infoBlockLine, { scaleX: 0, transformOrigin: `0% 50%`, opacity: 1 }, { scaleX: 1, transformOrigin: `0% 50%`, opacity: 1, duration: 0.5, ease: `expo.out` }, 0)
    infoBlockTl.current.fromTo(infoBlockTitle.current, { x: 10, opacity: 0, color: `#B59860` }, { x: 0, opacity: 1, color: `#B59860`, duration: 0.6, ease: `expo.out` }, 0.1)
    infoBlockTl.current.fromTo(infoBlockImage.current, { x: 10, opacity: 0 }, { x: 0, opacity: 1, duration: 0.8, ease: `expo.out` }, 0.16)
    infoBlockTl.current.pause(0)
    items.current = menuItems.map(item => {
      const id = item.getAttribute(`data-anchor`)
      return { id, el: item }
    })
    const onChangeAnchor = (id) => {
      anchor.current = id
      const current = items.current.filter(item => item.id ===  id)[0]
      if (current) gsap.to(dotEl.current, { y: current.el.offsetTop + 9, opacity: 1, scale: 1, duration: 1, ease: `expo.out`, overwrite: true })
      else gsap.to(dotEl.current, { y: 4, duration: 1, scale: 1, opacity: 1, ease: `expo.out`, overwrite: true })
      clearTimeout(repositionTimeout.current)
      repositionTimeout.current = setTimeout(repositionScroll, 200)
    }
    const onChangeMenuVisibility = () => {
      if (Store.menuIsVisible) {
        gsap.to(parentEl.current, {
          opacity: 1,
          ease: "expo.inOut",
          duration: 1,
        })
      } else {
        gsap.to(parentEl.current, {
          opacity: 0,
          ease: "expo.inOut",
          duration: 1,
        })
      }
    }
    const tl = gsap.timeline()
    tl.fromTo(containerEl.current, { opacity: 0 }, {duration: 1, opacity: 1, ease: `expo.out`, delay: 1.2 }, 0)
    tl.play(0)
    Store.on(constants.CHANGE_MENU_VISIBILITY, onChangeMenuVisibility)
    Store.on(constants.CHANGE_ANCHOR, onChangeAnchor)
    return () => {
      Store.off(constants.CHANGE_MENU_VISIBILITY, onChangeMenuVisibility)
      Store.off(constants.CHANGE_ANCHOR, onChangeAnchor)
    }
  }, [])
  const repositionScroll = () => {
    const current = items.current.filter(item => item.id ===  anchor.current)[0]
    if (current) {
      gsap.to(wrapperEl.current, {
        scrollTo:{ y: pointerToWindowRatio.current(current.el.offsetTop) },
        ease: "expo.out",
        duration: 0.8,
        overwrite: true
      })
    } else {
      gsap.to(wrapperEl.current, {
        scrollTo:{ y: pointerToWindowRatio.current(0) },
        duration: 0.8,
        ease: "expo.out",
        overwrite: true
      })
    }
  }
  const onMouseLeave = () => {
    repositionScroll()
    infoBlockTl.current.timeScale(4).reverse()
  }
  let uniqueDates = constants.DATES.filter((v,i,a)=>a.findIndex(t=>(t.data === v.data))===i);
  return (
    <menu ref={parentEl} className={`${className} `} id={`sidebar-menu`}>
        <div className={`tw-absolute tw-top-0 tw-left-0 tw-overflow-hidden tw-h-full`}>
          <div ref={infoEl} style={{ transform: `translateX(-600px)` }} className={`tw-fixed tw-top-0 tw-left-0 tw-pointer-events-none`}>
            <HoverItem photo={`/150.jpg`} text={``}/>
          </div>
          <div ref={containerEl} className={`tw-relative tw--ml-16 tw-w-full tw-opacity-0`}>
            <div onMouseLeave={onMouseLeave} className={`tw-fixed tw-z-50 tw-w-2/12 tw-top-0 tw-h-screen tw-overflow-y-hidden`} ref={wrapperEl}>
              <div className={`tw-relative`}>
                <div className={`tw-w-full force-overflow-x-visible`} ref={scrollingEl}>
                  <div className={`tw-h-72 tw-block tw-w-1/12 tw-text-right`}></div>
                  <div className={`tw-border-r-2 tw-border-gray tw-inline-block tw-text-right tw-left-1/2 tw-relative tw-px-3.5 tw-transform tw--translate-x-1/2`}>
                    <div ref={dotEl} style={{ right: `-7px`, top: `1px` }} className={`tw-bg-gold tw-block tw-w-3 tw-h-3 tw-rounded-full tw-absolute tw-transform tw-scale-0 tw-opacity-0`} />
                    <a className={`tw-text-tiny tw-text-gold tw-block tw-uppercase tw-cursor-pointer tw-pb-2 tw-font-sans`} href={`#home`}>{constants.UI.home}</a>
                    <ul className={``}>
                      {
                        uniqueDates.map((item, index) => {
                          return (
                            <ListItem item={item} key={`date-${index}-menu`} />
                          )
                        })
                      }
                    </ul>
                  </div>
                  <div className={`tw-h-60 tw-block tw-w-1/12`}/>
                </div>
              </div>
            </div>
          </div>
        </div>
    </menu>
  )
}

export default Menu
